import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { Settings2, Save, RefreshCw } from "lucide-react";

/**
 * Admin CMS for the debt pipeline / statuses / reasons.
 * Overrides completely replace defaults when saved — the admin can copy the
 * current list and edit, or leave blank to fall back to code defaults.
 */
const KEYS = [
  { key: "pipeline", label: "Pipeline stages (case board columns)", help: "Order matters. Minimum 3." },
  { key: "alt_statuses", label: "Alt statuses (case-terminal states)", help: "e.g. Follow-up, Cold, Dropped, Lost." },
  { key: "rejection_reasons", label: "Rejection reasons", help: "Shown as structured picklist when a case/application is rejected." },
  { key: "call_outcomes", label: "Call outcomes", help: "Structured outcome dropdown on the call log." },
  { key: "lender_app_stages", label: "Lender application stages", help: "Per-lender micro-pipeline." },
];

export default function AdminDebtConfig() {
  const [defaults, setDefaults] = useState(null);
  const [override, setOverride] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([
        api.get("/debt/config"),
        api.get("/admin/debt/config-overrides"),
      ]);
      setDefaults(c.data);
      setOverride(o.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      for (const { key } of KEYS) {
        const val = override[key];
        if (Array.isArray(val) && val.length > 0) body[key] = val;
        else if (val === null) body[key] = null;  // explicit revert
      }
      if (Object.keys(body).length === 0) {
        toast.error("Nothing to save.");
        setSaving(false); return;
      }
      await api.patch("/admin/debt/config-overrides", body);
      toast.success("Config overrides saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const copyDefault = (k) => setOverride((o) => ({ ...o, [k]: [...(defaults?.[k] || [])] }));
  // "Revert to default" now explicitly nulls the key so save() unsets it
  // server-side rather than leaving the override in place.
  const clearOverride = (k) => setOverride((o) => ({ ...o, [k]: null }));
  const setList = (k, text) => setOverride((o) => ({ ...o, [k]: text.split("\n").map((s) => s.trim()).filter(Boolean) }));

  if (loading || !defaults) {
    return <DashboardShell title="Debt Config" subtitle="Loading…"><div className="text-sm italic text-ink-muted">Loading…</div></DashboardShell>;
  }

  return (
    <DashboardShell title="Debt Config" subtitle="Admin · Pipeline & Picklists (CMS)">
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid="admin-debt-config">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs text-ink-muted">
            <Settings2 size={12} /> Overrides completely replace defaults. Leave blank to inherit code defaults. Rule of thumb: copy default → edit → save.
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1" data-testid="cfg-refresh"><RefreshCw size={11} /> Refresh</button>
            <Button variant="gold" onClick={save} disabled={saving} testId="cfg-save"><Save size={12} className="inline mr-1" />{saving ? "Saving…" : "Save overrides"}</Button>
          </div>
        </div>

        {KEYS.map(({ key, label, help }) => {
          const list = override[key];
          const isArr = Array.isArray(list);
          const isNull = list === null;  // explicit "revert to default" pending save
          const hasOverride = isArr || isNull;
          return (
            <div key={key} className="border border-navy/10 rounded bg-white/60 p-4" data-testid={`cfg-${key}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="overline">{label}</div>
                  <div className="text-[10px] font-mono text-ink-muted">{help}</div>
                  {isNull && <div className="text-[10px] font-mono text-amber-800 mt-0.5">Marked for revert on next save.</div>}
                </div>
                <div className="flex gap-2">
                  {!hasOverride && <button onClick={() => copyDefault(key)} className="text-[10px] font-heading text-navy hover:underline" data-testid={`cfg-copy-${key}`}>Copy default → override</button>}
                  {hasOverride && <button onClick={() => clearOverride(key)} className="text-[10px] font-heading text-rose-700 hover:underline" data-testid={`cfg-clear-${key}`}>Revert to default</button>}
                </div>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-mono text-ink-muted mb-1">Current default ({(defaults[key] || []).length})</div>
                  <div className="border border-dashed border-navy/15 rounded p-2 max-h-[220px] overflow-y-auto bg-white/70 space-y-1">
                    {(defaults[key] || []).map((v, i) => (
                      <div key={i} className="text-[11px] font-mono text-ink-muted">{i + 1}. {v}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-mono text-ink-muted mb-1">Override ({isArr ? list.length : isNull ? "revert pending" : "off"})</div>
                  <textarea
                    disabled={!isArr}
                    value={isArr ? list.join("\n") : ""}
                    onChange={(e) => setList(key, e.target.value)}
                    rows={Math.max(6, (isArr ? list.length : 0) + 1)}
                    className="w-full px-2 py-2 border border-navy/15 rounded text-xs font-mono text-ink disabled:bg-navy/[0.02]"
                    placeholder="One item per line"
                    data-testid={`cfg-textarea-${key}`}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DashboardShell>
  );
}
